import { ReactNode, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  PaperProps as OriginalPaperProps,
  SxProps,
  Button,
} from '@mui/material';
import theme from 'theme';
import { HTTPError } from 'services/api/entities';
import { Nullable } from 'types';
import {
  Checkbox,
  ErrorContent,
} from 'components/Modals/BaseDialog/components';
import { fontFamily } from 'theme/overrides/fontFamily';
import LoadingButton from 'components/LoadingButton';

export type SharedDialogProps = {
  title: string;
  checkboxText?: string;
  state: {
    isOpen: boolean;
    isLoading: boolean;
    isSubmitDisabled?: boolean;
    error: Nullable<HTTPError>;
  };
  action: {
    // Iset these to optional as I wanted to render different action buttons for every modal
    onSubmit?: () => void;
    onClose?: () => void;
    onExited?: () => void;
    checkboxAction?: (value: boolean) => void;
    closeButtonActionOverride?: () => void;
  };
  closeText?: Nullable<string>;
  submitText?: string;
  progressBar?: ReactNode;
  buttonVariant?: 'text' | 'contained' | 'outlined';
  buttonColor?: 'primary' | 'error' | 'info';
  textColor?: string;
  actionsContainerStyle?: SxProps;
  PaperProps?: OriginalPaperProps;
};

type BaseDialogProps = {
  children: ReactNode;
  titleStyle?: SxProps;
  submitButtonStyle?: SxProps;
  closeStyle?: SxProps;
} & SharedDialogProps;

/**
 * Base modal component that shares some logic for all other modals.
 */
const BaseDialog = ({
  title,
  titleStyle,
  checkboxText,
  state,
  action,
  children,
  PaperProps,
  closeText,
  submitText,
  progressBar,
  submitButtonStyle,
  closeStyle,
  actionsContainerStyle,
  buttonVariant,
  buttonColor,
  textColor,
}: BaseDialogProps) => {
  const { isOpen, isLoading, isSubmitDisabled, error } = state;

  const {
    onSubmit,
    onClose,
    onExited,
    checkboxAction,
    closeButtonActionOverride,
  } = action;
  const { sx: paperPropsSx, ...otherPaperProps } = PaperProps || {};

  const [isChecked, setIsChecked] = useState(false);

  const performCheckboxAction = () => {
    if (checkboxText && checkboxAction) checkboxAction(isChecked);
  };

  const closeAction = () => {
    performCheckboxAction();
    onClose?.();
  };

  const closeButtonAction = () => {
    performCheckboxAction();
    if (closeButtonActionOverride) {
      closeButtonActionOverride();

      return;
    }

    onClose?.();
  };

  const exitAction = () => {
    performCheckboxAction();
    onExited?.();
  };

  const getDialogTitle = (): string => {
    if (error) {
      return error.title;
    }
    return title;
  };

  const displayDialogContent = () => {
    if (error) {
      return (
        <ErrorContent error={error} closeButtonAction={closeButtonAction} />
      );
    }

    return (
      <>
        {children}

        <Checkbox
          checkboxText={checkboxText}
          isChecked={isChecked}
          onChange={() => setIsChecked(!isChecked)}
        />
      </>
    );
  };

  const shouldSubmitButtonRender = !!submitText;
  const shouldProgressBarRender = !!progressBar;

  return (
    <Dialog
      data-testid="base-dialog"
      sx={{
        color: theme.palette.grey[900],
      }}
      open={isOpen}
      disableEscapeKeyDown={isLoading}
      onClose={closeAction}
      TransitionProps={{
        onExited: exitAction,
      }}
      PaperProps={{
        sx: {
          borderRadius: theme.spacing(1),
          padding: theme.spacing(4),
          backgroundColor: theme.palette.grey[50],
          width: theme.spacing(75),
          minWidth: 160,
          ...paperPropsSx,
        },
        ...otherPaperProps,
      }}
    >
      <DialogTitle
        data-testid="base-dialog-title"
        sx={{
          color: theme.palette.grey[900],
          paddingInline: 0,
          textTransform: 'none',
          fontFamily: fontFamily.drukWideMedium,
          fontSize: 24,
          fontWeight: 500,
          lineHeight: 1.22,
          letterSpacing: 'normal',
          ...titleStyle,
        }}
      >
        {getDialogTitle()}
      </DialogTitle>

      {displayDialogContent()}

      <DialogActions
        sx={{ ...actionsContainerStyle, marginBottom: 0 }}
        disableSpacing
      >
        {closeText && (
          <Button
            sx={{
              ...closeStyle,
            }}
            variant="outlinedDark"
            onClick={closeButtonAction}
            data-testid="base-dialog-close-button"
          >
            {closeText}
          </Button>
        )}
        {shouldSubmitButtonRender && (
          <LoadingButton
            autoFocus
            type="submit"
            loading={isLoading}
            disabled={isSubmitDisabled}
            onClick={onSubmit}
            data-testid="base-dialog-submit-button"
            sx={{
              color: textColor || undefined,
              ...submitButtonStyle,
            }}
            variant={buttonVariant || 'rounded'}
            color={buttonColor || 'primary'}
          >
            {submitText}
          </LoadingButton>
        )}
        {shouldProgressBarRender && progressBar}
      </DialogActions>
    </Dialog>
  );
};

export default BaseDialog;
